import { motion } from "motion/react";
import { BarChart3, ShoppingCart, DollarSign, Users, TrendingUp, MapPin } from "lucide-react";

export function DashboardSection() {
  const metrics = [
    { label: "Revenue", value: "$47,892", change: "+23%", icon: DollarSign, color: "#10B981" },
    { label: "Conversões", value: "1,284", change: "+18%", icon: TrendingUp, color: "#00F5FF" },
    { label: "Active Carts", value: "342", change: "+12%", icon: ShoppingCart, color: "#FF4D00" },
    { label: "Visitantes", value: "8,492", change: "+15%", icon: Users, color: "#00F5FF" },
  ];

  const topLists = [
    { title: "Top Sources", items: ["Google Organic: 3,421", "Facebook Ads: 2,187", "Direct: 1,654", "Email: 1,230"] },
    { title: "Top Páginas", items: ["/produto-x: 2,840", "/checkout: 1,932", "/homepage: 1,654", "/categoria-y: 892"] },
    { title: "Geolocalização", items: ["São Paulo: 2,341", "Rio de Janeiro: 1,832", "Belo Horizonte: 954", "Curitiba: 743"] },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
      
      {/* Scan Line Effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00F5FF]/5 to-transparent h-32"
        animate={{ y: ["0%", "800%", "0%"] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <BarChart3 className="w-8 h-8 text-[#00F5FF]" />
              <span className="font-['Roboto_Mono'] text-[#00F5FF] text-sm tracking-[0.3em] uppercase">
                O QG da Sua Operação
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-5xl font-bold text-white mb-6">
              A Dashboard{" "}
              <span className="text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
                PhD Level
              </span>
            </h2>
            <p className="font-['Inter'] text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Não é um painel bonito apenas para olhar.{" "}
              <span className="text-[#10B981] font-semibold">É um cockpit de decisões.</span>
            </p>
            <p className="font-['Roboto_Mono'] text-sm text-gray-400 max-w-2xl mx-auto">
              Com o <span className="text-[#00F5FF]">Controller de Dashboard inteligente</span>, você tem uma visão 360° consolidada em uma interface que responde em milissegundos, graças ao nosso sistema de <span className="text-[#10B981]">Cache Inteligente</span> (15 minutos de refresh forçado).
            </p>
          </motion.div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="relative group"
              >
                <div
                  className="bg-[#0B0E14]/80 backdrop-blur-xl border rounded-2xl p-6 transition-all duration-300"
                  style={{
                    borderColor: `${metric.color}40`,
                    boxShadow: `0 0 30px ${metric.color}15, inset 0 0 50px ${metric.color}05`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      backgroundColor: `${metric.color}20`,
                      boxShadow: `0 0 20px ${metric.color}30`,
                    }}
                  >
                    <metric.icon className="w-6 h-6" style={{ color: metric.color }} />
                  </div>

                  {/* Label */}
                  <div className="font-['Roboto_Mono'] text-xs text-gray-400 mb-2 uppercase tracking-wider">
                    {metric.label}
                  </div>

                  {/* Value */}
                  <div className="font-['Sora'] text-3xl font-bold text-white mb-2">
                    {metric.value}
                  </div>

                  {/* Change */}
                  <div
                    className="font-['Roboto_Mono'] text-sm font-semibold"
                    style={{ color: metric.color }}
                  >
                    {metric.change} vs. último mês
                  </div>

                  {/* Glow on Hover */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${metric.color}20, transparent 70%)`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Main Dashboard Visual */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0B0E14]/60 backdrop-blur-xl border border-[#00F5FF]/30 rounded-3xl p-8 mb-12"
            style={{
              boxShadow: "0 0 60px rgba(0,245,255,0.15), inset 0 0 80px rgba(0,245,255,0.05)",
            }}
          >
            {/* Chart Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#00F5FF]/20">
              <h3 className="font-['Sora'] text-2xl font-bold text-white">
                Trend Chart: Users vs Sessions vs Visits
              </h3>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#00F5FF]" />
                  <span className="font-['Roboto_Mono'] text-xs text-gray-400">Users</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                  <span className="font-['Roboto_Mono'] text-xs text-gray-400">Sessions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF4D00]" />
                  <span className="font-['Roboto_Mono'] text-xs text-gray-400">Visits</span>
                </div>
              </div>
            </div>

            {/* Simplified Chart Visual */}
            <div className="relative h-64">
              <div className="absolute inset-0 flex items-end justify-around gap-2">
                {[45, 62, 38, 75, 55, 82, 68, 90, 72, 85, 78, 95].map((height, i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="flex-1 flex flex-col items-center gap-1"
                    style={{ transformOrigin: "bottom" }}
                  >
                    <div
                      className="w-full rounded-t-lg"
                      style={{
                        height: `${height}%`,
                        background: `linear-gradient(to top, #00F5FF, #10B981)`,
                        boxShadow: `0 0 20px rgba(0,245,255,0.3)`,
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Highlighted Metric */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-8 bg-[#FF4D00]/10 border border-[#FF4D00]/40 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-['Roboto_Mono'] text-xs text-[#FF4D00] mb-2 uppercase tracking-wider">
                    Atenção Urgente
                  </h4>
                  <p className="font-['Sora'] text-2xl font-bold text-white">
                    Abandoned Carts: $12,450
                  </p>
                  <p className="font-['Inter'] text-sm text-gray-400 mt-1">
                    Valor em carrinhos abandonados nas últimas 24h
                  </p>
                </div>
                <ShoppingCart className="w-16 h-16 text-[#FF4D00] opacity-50" />
              </div>
            </motion.div>
          </motion.div>

          {/* Top Lists Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {topLists.map((list, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-[#0B0E14]/80 backdrop-blur-xl border border-[#00F5FF]/20 rounded-2xl p-6"
                style={{
                  boxShadow: "0 0 30px rgba(0,245,255,0.1), inset 0 0 50px rgba(0,245,255,0.03)",
                }}
              >
                <h4 className="font-['Sora'] text-xl font-bold text-[#00F5FF] mb-4">
                  {list.title}
                </h4>
                <div className="space-y-3">
                  {list.items.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 + i * 0.1 }}
                      className="flex items-center gap-3 p-3 bg-[#0B0E14]/60 rounded-lg border border-[#00F5FF]/10 hover:border-[#00F5FF]/30 transition-all"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#10B981]" />
                      <span className="font-['Roboto_Mono'] text-sm text-gray-300 flex-1">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="font-['Sora'] text-2xl text-white">
              O que você vê no primeiro olhar:{" "}
              <span className="text-[#10B981] font-bold">Receita</span>,{" "}
              <span className="text-[#00F5FF] font-bold">Conversões</span>,{" "}
              <span className="text-[#FF4D00] font-bold">Carrinhos</span> e{" "}
              <span className="text-[#00F5FF] font-bold">Inteligência UTM</span>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
